import { SxProps } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isSameDay } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Nullable } from "../../../../common/types/nullable";
import { customerOrgNamesResult } from "../../../customer/customerAPI";
import { fetchCustomerOrgNames } from "../../../customer/customerOrganizationsSlice";
import {
  AccumDateData,
  AccumulationsForContinuousEmploymentFilters,
  AccumulationsForEmploymentFilters,
  AccumulationsForEmploymentResponse,
} from "../../../employment/employmentApi";
import {
  delAccumulationDelta,
  fetchAccumulationsForContinuousEmployment,
  fetchAccumulationsForEmployment,
} from "../../../employment/employmentSlice";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import { fetchAssignmentsByTalentId } from "../AssignmentAdditionalInfo/AssignmentsSlice";
import {
  ACCUMULATION_OPTIONS,
  AccumulationCalculationType,
} from "./accumulationOptions";
import AccumulationsEmptyState from "./AccumulationsEmptyState";
import AccumulationsTable from "./accumulationsTable/AccumulationsTable";
import ActionButtons from "./ActionButtons";
import AccumulationsEditComponents from "./edit/AccumulationsEditComponents";
import AccumulationsFields from "./edit/editForm/AccumulationsFields";
import { combineGroupMathAccumulations } from "./helpers";
import { AccumulationType, EventSummary, SelectedAccumulation } from "./types";
import { isoFormat } from "../../../../common/helpers/formatDateTime";

type Props = {
  accumulationType: AccumulationType;
  edit: boolean;
  onEdit: () => void;
  onClose: () => void;
  onCancel?: () => void;
  xs?: [number, number];
  tableProps?: { sx: SxProps };
  setEditDisabled?: (value: boolean) => void;
  editDisabled?: boolean;
  continuousEmploymentId?: string;
  employmentId?: string;
};

const Accumulations = ({
  accumulationType,
  onClose,
  edit,
  onEdit,
  onCancel,
  xs = [0, 0],
  tableProps,
  setEditDisabled,
  editDisabled,
  continuousEmploymentId,
  employmentId,
}: Props) => {
  const [tableData, setTableData] =
    useState<AccumulationsForEmploymentResponse>();
  const [selectedAccumulation, setSelectedAccumulation] =
    useState<Nullable<SelectedAccumulation>>(null);
  const [selectedAccumulationRow, setSelectedAccumulationRow] =
    useState<Nullable<AccumDateData>>(null);
  const [customerOrgNames, setCustomerOrgNames] = useState<
    customerOrgNamesResult[]
  >([]);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    deltaId: "",
  });
  const [hasFetched, setHasFetched] = useState(false);

  const { status: employmentStatus } = useAppSelector(
    (state) => state.employment,
  );

  const { status: customerOrganizationsStatus } = useAppSelector(
    (state) => state.customerOrganizations,
  );

  const isLoading =
    employmentStatus === "pending" || customerOrganizationsStatus === "pending";

  const { canEditAccumulations } = useContext(AccessRightContext);

  const { t } = useTranslation();
  const { talentId } = useParams();

  const dispatch = useAppDispatch();
  const methods = useFormContext();

  const {
    watch,
    formState: { isValid, errors },
  } = methods;

  useEffect(() => {
    if (setEditDisabled) {
      if (tableData) {
        setEditDisabled(false);
      } else {
        setEditDisabled(true);
      }
    }
  }, [
    tableData,
    setEditDisabled,
    selectedAccumulation?.isInternal,
    selectedAccumulation?.defaultValues,
  ]);

  useEffect(() => {
    const hasAssignmentScope = Array.isArray(selectedAccumulation?.scope)
      ? selectedAccumulation?.scope.some((s) => s.endsWith("ASSIGNMENT"))
      : selectedAccumulation?.scope?.endsWith("ASSIGNMENT");

    if (hasAssignmentScope && isValid) {
      const start = watch("dateFrom");
      const end = watch("dateTo");
      const formattedStart = format(start, isoFormat);
      const formattedEnd = format(end, isoFormat);

      if (talentId) {
        const params = {
          talentId,
          start: formattedStart,
          end: formattedEnd,
        };

        dispatch(fetchAssignmentsByTalentId(params));
      }
    }
  }, [
    selectedAccumulation?.scope,
    dispatch,
    talentId,
    watch,
    employmentId,
    isValid,
  ]);

  const deleteLocalDelta = (deltaId: string) => {
    const updatedData = tableData?.data.map((item) => ({
      ...item,
      rows: item.rows.filter((row) => row.deltaId !== deltaId),
    })) as AccumDateData[];

    const filterEmptyRows = updatedData.filter((item) => !isEmpty(item.rows));

    const updatedTableData = {
      meta: {
        keys: tableData?.meta?.keys || [],
        stateNames: tableData?.meta?.stateNames || [],
      },
      data: filterEmptyRows,
    };

    setTableData(updatedTableData);
  };

  const onDeleteDelta = (deltaId: string) => {
    if (deltaId) {
      dispatch(delAccumulationDelta({ id: deltaId })).then((action) => {
        if (isFulfilled(action)) {
          deleteLocalDelta(deltaId);
          closeConfimationDialog();
        }
      });
    }
  };

  const openConfimationDialog = (deltaId: string) => {
    setConfirmationDialog({ open: true, deltaId });
  };

  const closeConfimationDialog = () =>
    setConfirmationDialog({ open: false, deltaId: "" });

  const onConfirm = () => onDeleteDelta(confirmationDialog.deltaId);

  const updateLocalTableData = (date: string, data: EventSummary[]) => {
    const existingDate = tableData?.data.find((item) =>
      isSameDay(new Date(item.date as string), new Date(date)),
    );

    if (isEmpty(existingDate)) {
      // add new date row
      const newRow: AccumDateData = {
        date,
        rows: data,
      };

      const updatedTableData = {
        meta: {
          keys: tableData?.meta?.keys || [],
          stateNames: tableData?.meta?.stateNames || [],
        },
        data: tableData?.data ? [...tableData.data, newRow] : [newRow],
      };

      setTableData(() => updatedTableData);
    } else {
      // add new row to existing date
      const updatedIndex = tableData?.data.findIndex(
        (item) => item.date === date,
      ) as number;

      const updatedData = tableData?.data
        ? tableData?.data.map((item, index) =>
            index === updatedIndex
              ? { ...item, rows: [...item.rows, ...data] }
              : item,
          )
        : [];
      const updatedTableData = {
        meta: {
          keys: tableData?.meta?.keys || [],
          stateNames: tableData?.meta?.stateNames || [],
        },
        data: updatedData,
      };
      setTableData(() => updatedTableData);
    }
  };

  const fetchAccumulations = async (
    filters:
      | AccumulationsForContinuousEmploymentFilters
      | AccumulationsForEmploymentFilters,
  ): Promise<AccumulationsForEmploymentResponse | null> => {
    if ("continuousEmploymentId" in filters) {
      const accumulationsForContinuousEmployment = await dispatch(
        fetchAccumulationsForContinuousEmployment(filters),
      );

      if (isFulfilled(accumulationsForContinuousEmployment)) {
        return accumulationsForContinuousEmployment.payload;
      }
      return null;
    } else {
      const accumulationsForEmployment = await dispatch(
        fetchAccumulationsForEmployment(filters),
      );
      if (isFulfilled(accumulationsForEmployment)) {
        return accumulationsForEmployment.payload;
      }
      return null;
    }
  };

  const searchAccumluations = async () => {
    const from = watch("dateFrom");
    const to = watch("dateTo");

    if (isEmpty(errors) && isValid) {
      if (
        selectedAccumulation?.calculationType ===
        AccumulationCalculationType.GROUP_MATH
      ) {
        // Multiple states in GROUP_MATH type - one API call per state
        const stateNames = Array.isArray(selectedAccumulation.stateNames)
          ? selectedAccumulation.stateNames
          : [selectedAccumulation.stateNames];
        const scopes = Array.isArray(selectedAccumulation.scope)
          ? selectedAccumulation.scope
          : [selectedAccumulation.scope];

        // Make multiple API calls for each stateName/scope pair
        const accumulationsPromises = stateNames.map((stateName, index) => {
          const scope = scopes[index];
          const params = {
            continuousEmployment: {
              filters: {
                scope,
                stateNames: stateName,
                continuousEmploymentId,
                from: format(from, isoFormat),
                to: format(to, isoFormat),
              } as AccumulationsForContinuousEmploymentFilters,
            },
            employment: {
              filters: {
                scope,
                stateNames: stateName,
                employmentId,
                from: format(from, isoFormat),
                to: format(to, isoFormat),
              } as AccumulationsForEmploymentFilters,
            },
          };
          return fetchAccumulations(params[accumulationType].filters);
        });

        const allAccumulations = await Promise.all(accumulationsPromises);

        // Combine results from multiple API calls
        if (allAccumulations.every((acc) => acc !== null)) {
          const combinedAccumulations = combineGroupMathAccumulations(
            allAccumulations as AccumulationsForEmploymentResponse[],
            stateNames,
          );
          setTableData(combinedAccumulations);

          const customerOrgIds =
            !isEmpty(combinedAccumulations) &&
            combinedAccumulations.meta?.keys.includes("custOrgId")
              ? combinedAccumulations?.data
                  .map((it) => it.rows.map((row) => row.custOrgId))
                  .flat()
              : [];

          if (!isEmpty(customerOrgIds)) {
            dispatch(
              fetchCustomerOrgNames({ ids: customerOrgIds.toString() }),
            ).then((action) => {
              if (isFulfilled(action)) {
                setCustomerOrgNames(action.payload);
              }
              setHasFetched(true);
            });
          } else {
            setHasFetched(true);
          }
        }
      } else {
        const params = {
          continuousEmployment: {
            filters: {
              scope: selectedAccumulation?.scope as string,
              stateNames: Array.isArray(selectedAccumulation?.stateNames)
                ? selectedAccumulation?.stateNames.join(",")
                : (selectedAccumulation?.stateNames as string),
              continuousEmploymentId,
              from: format(from, isoFormat),
              to: format(to, isoFormat),
            } as AccumulationsForContinuousEmploymentFilters,
          },
          employment: {
            filters: {
              scope: selectedAccumulation?.scope as string,
              stateNames: Array.isArray(selectedAccumulation?.stateNames)
                ? selectedAccumulation?.stateNames.join(",")
                : (selectedAccumulation?.stateNames as string),
              employmentId,
              from: format(from, isoFormat),
              to: format(to, isoFormat),
            } as AccumulationsForEmploymentFilters,
          },
        };
        const accumulations = await fetchAccumulations(
          params[accumulationType].filters,
        );

        if (accumulations) {
          setTableData(accumulations);

          const customerOrgIds =
            !isEmpty(accumulations) &&
            accumulations.meta?.keys.includes("custOrgId")
              ? accumulations?.data
                  .map((it) => it.rows.map((row) => row.custOrgId))
                  .flat()
              : [];

          if (!isEmpty(customerOrgIds)) {
            dispatch(
              fetchCustomerOrgNames({ ids: customerOrgIds.toString() }),
            ).then((action) => {
              if (isFulfilled(action)) {
                setCustomerOrgNames(action.payload);
              }
              setHasFetched(true);
            });
          } else {
            setHasFetched(true);
          }
        }
      }
    }
  };

  const handleAccumulationChange = (value: string | null) => {
    setTableData(undefined);

    if (setEditDisabled) {
      setEditDisabled(true);
    }

    if (!value) return setSelectedAccumulation(() => null);

    const accumulationDetails = ACCUMULATION_OPTIONS.find(
      (option) => option.value === value,
    );

    if (accumulationDetails) {
      setSelectedAccumulation({
        value: accumulationDetails.value,
        scope: accumulationDetails.scope,
        stateNames: accumulationDetails.stateNames,
        isInternal: accumulationDetails.isInternal,
        displayNames: accumulationDetails.displayNames,
        ...(accumulationDetails.calculationType && {
          calculationType: accumulationDetails.calculationType,
        }),
        defaultValues: accumulationDetails.defaultValues,
      });
    }
  };

  const handleSelectedAccumulationRowChange = (row: AccumDateData) =>
    setSelectedAccumulationRow(row);

  const renderEmptyState = hasFetched && isEmpty(tableData?.data) && !isLoading;
  const editDisabledFinal =
    !isValid || isLoading || !tableData || !!editDisabled;

  return (
    <>
      <AccumulationsFields
        accumulationType={accumulationType}
        searchAccumluations={searchAccumluations}
        handleAccumulationChange={handleAccumulationChange}
        xsLabel={xs[0]}
        xsField={xs[1]}
      />
      {tableData && !isEmpty(tableData.data) && !isLoading && (
        <AccumulationsTable
          tableData={tableData}
          customerOrgNames={customerOrgNames}
          openConfimationDialog={openConfimationDialog}
          tableProps={tableProps}
          selectedAccumulation={selectedAccumulation}
          handleSelectedAccumulationRowChange={
            handleSelectedAccumulationRowChange
          }
          onEdit={onEdit}
          canEdit={canEditAccumulations}
        />
      )}
      <AccumulationsEmptyState xs={xs} renderEmptyState={renderEmptyState} />
      {accumulationType === "continuousEmployment" && (
        <ActionButtons
          editDisabled={editDisabledFinal}
          onEdit={onEdit}
          onCancel={onCancel as () => void}
          canEdit={canEditAccumulations}
        />
      )}
      <DialogComponent
        open={edit}
        onClose={onClose}
        dragHandleSelector="drag-handle-accumulations"
        autoWidth
      >
        {tableData && edit && (
          <AccumulationsEditComponents
            accumulationType={accumulationType}
            employmentId={employmentId}
            contEmpId={continuousEmploymentId}
            onClose={onClose}
            tableData={tableData}
            updateLocalTableData={updateLocalTableData}
            selectedAccumulation={selectedAccumulation}
            selectedAccumulationRow={selectedAccumulationRow}
          />
        )}
      </DialogComponent>
      <ConfirmationDialog
        isOpen={confirmationDialog.open}
        title={t("talentProfile:deleteAccum")}
        contentText={t("talentProfile:deleteAccumContentText")}
        confirmText={t("common:delete")}
        onConfirm={onConfirm}
        onCancel={closeConfimationDialog}
        isLoading={isLoading}
      />
    </>
  );
};

export default Accumulations;
